interface NumberControlProps {
  name: string
  value: number
  min?: number
  /** Optional ceiling. Left off, the control counts up without a bound (bedrooms, bathrooms). */
  max?: number
  onChange: (value: number) => void
  /**
   * Row label (e.g. "Adults", "Pets", "Bedrooms") folded into each button's
   * accessible name — "Decrease adults" / "Increase adults" — so automation
   * and assistive tech can tell rows apart (#1003 item 3b: a bare
   * title="Decrease/Increase Value" gave every row the same generic name and
   * carried no role, so getByRole('button', ...) queries couldn't find it).
   */
  label: string
}

export default function NumberControl({ name, value, min = 0, max, onChange, label }: NumberControlProps) {
  const atMax = max !== undefined && value >= max
  const lowerLabel = label.toLowerCase()
  const decrease = () => onChange(Math.max(min, value - 1))
  const increase = () => { if (!atMax) onChange(max === undefined ? value + 1 : Math.min(max, value + 1)) }
  // role="button" on a non-native element needs its own keyboard operability
  // (WAI-ARIA authoring practices): Tab must reach it and Enter/Space must
  // activate it, same as onClick — a named-but-unreachable control is worse
  // than the old unnamed one (#1003 item 3b follow-up). Space is prevented
  // so the popover body doesn't scroll on activation.
  const activateOnKey = (handler: () => void) => (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    if (e.key === ' ') e.preventDefault()
    handler()
  }
  return (
    <div className="hfse-number-with-control">
      <div
        className="reduce-btn"
        role="button"
        tabIndex={0}
        title="Decrease Value"
        aria-label={`Decrease ${lowerLabel}`}
        onClick={decrease}
        onKeyDown={activateOnKey(decrease)}
      >
        -
      </div>
      <input
        type="text"
        name={name}
        className="number-input"
        value={value}
        readOnly
      />
      <div
        className={`increase-btn${atMax ? ' hfse-number-control-disabled' : ''}`}
        role="button"
        tabIndex={0}
        title="Increase Value"
        aria-label={`Increase ${lowerLabel}`}
        aria-disabled={atMax || undefined}
        onClick={increase}
        onKeyDown={activateOnKey(increase)}
      >
        +
      </div>
    </div>
  )
}
